import { computed, ref, type Ref, onBeforeUnmount } from 'vue';
import type * as THREE from 'three/webgpu';
import { findAncestorSketchGroupId, isSketchEllipseCommand, SketchDrawingTool } from '@puppycad/cad-core';
import { SketchCornerMode } from '@/core';
import { useDrawingStore } from './useDrawing';
import { useSketchStateStore } from './useSketchState';
import { useGetSelectLine } from './selection/useGetSelectLine';
import { useGetSelectCircle } from './selection/useGetSelectCircle';
import { useGetSelectArc } from './selection/useGetSelectArc';
import { useGetSelectEllipse } from './selection/useGetSelectEllipse';
import { useGetSelectSpline } from './selection/useGetSelectSpline';
import { useGetSelectStyleSpline } from './selection/useGetSelectStyleSpline';
import { useGetSelectSlot } from './selection/useGetSelectSlot';
import { useGetSelectPoint } from './selection/useGetSelectPoint';
import { useGetSelectRectangle } from './selection/useGetSelectRectangle';
import { storeToRefs } from 'pinia';
import type { Subscription } from 'rxjs';

/**
 * useSketchPropertyVisible hook 的入参配置。
 * 仅传入 Editor.vue 中的本地响应式引用，store 中的值由 hook 内部获取。
 */
export interface UseSketchPropertyVisibleOptions {
  /** 倒角是否激活。 */
  sketchCornerEnable: Ref<boolean>;
  /** 当前活动的操作命令。 */
  activeCommand: Ref<string | null>;
  /** 当前草图绘制命令，椭圆族通过它区分抛物线等子模式。 */
  currentDrawingCommand?: Ref<string | null>;
  /** 多实体关系面板候选态（当前选中数量大于 1）。 */
  multipleEntitiesBoxSelected: Ref<boolean>;
  /** 选中的草图实体端点索引（矩形角点场景）。 */
  selectedSketchEntityEndpointIndex?: Ref<number | null>;
}

/**
 * 计算所有草图工具属性面板可见性的 hook。
 * 每个属性面板对应一个 computed boolean，逻辑：
 * - 当前绘制工具匹配时显示
 * - 或者草图模式下通过事件中心检测到选中了对应类型的实体时显示
 * - 其他绘制工具激活时不显示
 * - 合并了线属性面板可见性（原 useLinePropertyVisible）
 * @param options - 需要从外部传入的响应式依赖
 * @returns 各面板可见性 computed 属性集合
 */
export function useSketchPropertyVisible(options: UseSketchPropertyVisibleOptions) {
  const drawingStore = useDrawingStore();
  const { isDrawingMode, currentDrawingTool, drawingPointCount, lineDrawingPointCount } = storeToRefs(drawingStore);

  const sketchStateStore = useSketchStateStore();
  const { isSketchActive, currentSketchGroupId } = storeToRefs(sketchStateStore);

  //------ 事件中心选择检测 开始------
  const { onLineChanged } = useGetSelectLine();
  const { onChanged: onCircleChanged } = useGetSelectCircle();
  const { onChanged: onArcChanged } = useGetSelectArc();
  const { onChanged: onEllipseChanged } = useGetSelectEllipse();
  const { onChanged: onSplineChanged } = useGetSelectSpline();
  const { onChanged: onStyleSplineChanged } = useGetSelectStyleSpline();
  const { onChanged: onSlotChanged } = useGetSelectSlot();
  const { onPointChanged } = useGetSelectPoint();
  const { onChanged: onRectangleChanged } = useGetSelectRectangle();

  /** 各实体类型是否被选中的响应式标记。 */
  const hasLineSelected = ref(false);
  const hasCircleSelected = ref(false);
  const hasArcSelected = ref(false);
  const hasEllipseSelected = ref(false);
  const hasSplineSelected = ref(false);
  const hasStyleSplineSelected = ref(false);
  const hasSlotSelected = ref(false);
  const hasPointSelected = ref(false);
  const hasRectangleSelected = ref(false);

  //------ foreign sketch 识别 开始------
  /**
   * 当前选中对象是否属于"外部草图"（即非当前活动草图）。
   *
   * 设计说明：
   * 1. 草图编辑态下，hover / click 外部草图实体仍保留视觉反馈；但属性面板不应弹出该实体的属性，
   *    避免用户误以为"点谁都可以改"；
   * 2. 判定内核统一复用 cad-core 的 `findAncestorSketchGroupId`（`@puppycad/cad-core` 公共出口）；
   *    不在 web-client 重复实现所属草图解析逻辑；
   * 3. 主分支现有 hook 是多条订阅流结构（`onLineChanged` / `onPointChanged` 等），foreign 判定
   *    在每条订阅内同步更新。多选混合场景不单独处理，foreign 以最后一次推送为准；
   * 4. 各 property visible 计算处在最前置做 early return，避免与 sketch-trim 独占分支判定顺序冲突。
   */
  const hasForeignSketchSelection = ref(false);

  /**
   * 根据当前推送的实体更新 foreign sketch 标记。
   *
   * @param entity 事件中心推送的实体（THREE.Object3D 或 null）。
   */
  function updateForeignSketchSelection(entity: THREE.Object3D | null): void {
    if (!isSketchActive.value || !currentSketchGroupId.value || !entity) {
      hasForeignSketchSelection.value = false;
      return;
    }
    const selectedSketchGroupId = findAncestorSketchGroupId(entity);
    hasForeignSketchSelection.value = Boolean(
      selectedSketchGroupId && selectedSketchGroupId !== currentSketchGroupId.value,
    );
  }
  //------ foreign sketch 识别 结束------

  /** 事件订阅列表。 */
  const entitySubscriptions: Subscription[] = [];

  // 订阅各类型选中变化（BehaviorSubject 会立即推送当前值）
  entitySubscriptions.push(
    onLineChanged().subscribe((line) => {
      hasLineSelected.value = line !== null;
      updateForeignSketchSelection(line as unknown as THREE.Object3D | null);
    }),
    onCircleChanged().subscribe((circle) => {
      hasCircleSelected.value = circle !== null;
      updateForeignSketchSelection(circle as unknown as THREE.Object3D | null);
    }),
    onArcChanged().subscribe((arc) => {
      hasArcSelected.value = arc !== null;
      updateForeignSketchSelection(arc as unknown as THREE.Object3D | null);
    }),
    onEllipseChanged().subscribe((ellipse) => {
      hasEllipseSelected.value = ellipse !== null;
      updateForeignSketchSelection(ellipse as unknown as THREE.Object3D | null);
    }),
    onSplineChanged().subscribe((spline) => {
      hasSplineSelected.value = spline !== null;
      updateForeignSketchSelection(spline as unknown as THREE.Object3D | null);
    }),
    onStyleSplineChanged().subscribe((styleSpline) => {
      hasStyleSplineSelected.value = styleSpline !== null;
      updateForeignSketchSelection(styleSpline as unknown as THREE.Object3D | null);
    }),
    onSlotChanged().subscribe((slot) => {
      hasSlotSelected.value = slot !== null;
      updateForeignSketchSelection(slot as unknown as THREE.Object3D | null);
    }),
    onPointChanged().subscribe((point) => {
      hasPointSelected.value = point !== null;
      updateForeignSketchSelection(point as unknown as THREE.Object3D | null);
    }),
    onRectangleChanged().subscribe((rectangle) => {
      hasRectangleSelected.value = rectangle !== null;
      updateForeignSketchSelection(rectangle as unknown as THREE.Object3D | null);
    }),
  );

  onBeforeUnmount(() => {
    entitySubscriptions.forEach((sub) => sub.unsubscribe());
    entitySubscriptions.length = 0;
  });
  //------ 事件中心选择检测 结束------

  /**
   * 通用草图工具面板可见性判定。
   * 规则：
   * 1. 其他绘制工具/模式激活时 → 不显示
   * 2. 目标工具激活时 → 显示
   * 3. 草图模式下通过事件中心检测到选中了匹配实体 → 显示
   * @param tool - 目标绘制工具枚举
   * @param hasSelected - 对应实体是否被选中的响应式引用
   * @returns 面板是否可见
   */
  function isToolOrSelectionVisible(
    tool: SketchDrawingTool | string,
    hasSelected?: Ref<boolean>,
    isCurrentCommandActive = false,
  ): boolean {
    // 外部草图选中态下不弹草图属性面板
    if (hasForeignSketchSelection.value) {
      return false;
    }
    // 多实体关系面板候选态激活时不显示，避免与几何关系面板并存。
    if (options.multipleEntitiesBoxSelected.value) {
      return false;
    }
    const isToolActive = currentDrawingTool.value === tool;
    const isDrawingCommandActive = isDrawingMode.value && isCurrentCommandActive;
    // 其他绘制工具/模式激活时不显示
    if ((isDrawingMode.value || currentDrawingTool.value) && !isToolActive && !isDrawingCommandActive) {
      return false;
    }
    // 目标工具激活时显示
    if (isToolActive || isDrawingCommandActive) {
      return true;
    }
    // 草图模式下通过事件中心检测到选中了匹配实体时显示
    if (hasSelected && isSketchActive.value && hasSelected.value) {
      return true;
    }
    return false;
  }

  //------ 线属性面板可见性（合并自 useLinePropertyVisible） 开始------
  /** 线属性面板是否可见 */
  const linePropertyVisible = computed<boolean>(() => {
    // 外部草图选中态下不弹草图属性面板
    if (hasForeignSketchSelection.value) {
      return false;
    }
    // 倒角模式激活时不显示
    if (options.sketchCornerEnable.value) {
      return false;
    }
    // 倒角/圆角命令时不显示
    if (
      options.activeCommand.value === SketchCornerMode.Fillet ||
      options.activeCommand.value === SketchCornerMode.Chamfer
    ) {
      return false;
    }
    // 多实体关系面板候选态激活时不显示
    if (options.multipleEntitiesBoxSelected.value) {
      return false;
    }
    // 矩形集合选中时优先显示矩形属性，避免底层四条线抢占 PropertyManager。
    if (hasRectangleSelected.value) {
      return false;
    }
    // 判断当前绘制工具是否为线
    const isToolActive = currentDrawingTool.value === SketchDrawingTool.Line;
    // 其他绘制工具/模式激活时不显示
    if ((isDrawingMode.value || currentDrawingTool.value) && !isToolActive) {
      return false;
    }
    // 正在绘制线且已有绘制点时显示
    if (isToolActive && (lineDrawingPointCount.value ?? 0) > 0) {
      return true;
    }
    // 草图模式下选中了线段时显示
    if (isSketchActive.value && hasLineSelected.value) {
      return true;
    }
    return false;
  });
  //------ 线属性面板可见性（合并自 useLinePropertyVisible） 结束------

  /** 圆弧属性面板是否可见（绘制时或选中圆弧时） */
  const arcPropertyVisible = computed<boolean>(() => {
    return isToolOrSelectionVisible(SketchDrawingTool.Arc, hasArcSelected);
  });

  /** 圆属性面板是否可见（绘制时或选中圆时） */
  const circlePropertyVisible = computed<boolean>(() => {
    return isToolOrSelectionVisible(SketchDrawingTool.Circle, hasCircleSelected);
  });

  /** 椭圆属性面板是否可见（绘制时或选中椭圆时） */
  const ellipsePropertyVisible = computed<boolean>(() => {
    const currentCommand = options.currentDrawingCommand?.value ?? null;
    const isEllipseFamilyCommandActive = Boolean(
      currentCommand
      && isSketchEllipseCommand(currentCommand)
      && (!currentDrawingTool.value || currentDrawingTool.value === SketchDrawingTool.Ellipse),
    );
    return isToolOrSelectionVisible(
      SketchDrawingTool.Ellipse,
      hasEllipseSelected,
      isEllipseFamilyCommandActive,
    );
  });

  /** 多边形属性面板是否可见（仅多边形工具激活时） */
  const polygonPropertyVisible = computed<boolean>(() => {
    return isToolOrSelectionVisible(SketchDrawingTool.Polygon);
  });

  /** 矩形属性面板是否可见（绘制时或选中矩形集合时） */
  const rectanglePropertyVisible = computed<boolean>(() => {
    // 外部草图选中态下不弹草图属性面板
    if (hasForeignSketchSelection.value) {
      return false;
    }
    const isToolActive = currentDrawingTool.value === SketchDrawingTool.Rectangle;
    // 其他绘制工具/模式激活时不显示
    if ((isDrawingMode.value || currentDrawingTool.value) && !isToolActive) {
      return false;
    }
    // 目标工具激活时显示绘制参数面板
    if (isToolActive) {
      return true;
    }
    /**
     * 矩形轮廓点击会被底层解析为同一矩形集合关联的多对象选择。
     * 此处允许矩形语义优先显示，避免被多实体关系候选态误拦截。
     */
    if (isSketchActive.value && hasRectangleSelected.value) {
      return true;
    }
    return false;
  });

  /** 样条曲线属性面板是否可见（绘制时或选中样条时） */
  const splinePropertyVisible = computed<boolean>(() => {
    return isToolOrSelectionVisible(SketchDrawingTool.Spline, hasSplineSelected);
  });

  /** 样式样条曲线属性面板是否可见（工具激活且 pointCount=0，或选中样式样条时） */
  const styleSplinePropertyVisible = computed<boolean>(() => {
    // 外部草图选中态下不弹草图属性面板
    if (hasForeignSketchSelection.value) {
      return false;
    }
    // 多实体关系面板候选态激活时不显示
    if (options.multipleEntitiesBoxSelected.value) {
      return false;
    }
    const isToolActive = currentDrawingTool.value === SketchDrawingTool.StyleSpline;
    // 选中样式样条时，优先显示实体属性面板，避免被“插入态”显隐规则覆盖。
    if (isSketchActive.value && hasStyleSplineSelected.value && (isToolActive || !currentDrawingTool.value)) {
      return true;
    }
    // 其他绘制工具/模式激活时不显示
    if ((isDrawingMode.value || currentDrawingTool.value) && !isToolActive) {
      return false;
    }
    // 工具激活时，仅在未开始绘制（pointCount=0）时显示
    if (isToolActive) {
      return (drawingPointCount.value ?? 0) === 0;
    }
    // 草图模式下选中了样式样条时显示
    if (isSketchActive.value && hasStyleSplineSelected.value) {
      return true;
    }
    return false;
  });

  /** 槽口属性面板是否可见（绘制时或选中槽口时） */
  const slotPropertyVisible = computed<boolean>(() => {
    return isToolOrSelectionVisible(SketchDrawingTool.Slot, hasSlotSelected);
  });

  /** 点属性面板是否可见（绘制时或选中点/矩形角点时） */
  const pointPropertyVisible = computed<boolean>(() => {
    // 外部草图选中态下不弹草图属性面板
    if (hasForeignSketchSelection.value) {
      return false;
    }
    // 多实体关系面板候选态激活时不显示
    if (options.multipleEntitiesBoxSelected.value) {
      return false;
    }
    const isToolActive = currentDrawingTool.value === SketchDrawingTool.Point;
    // 其他绘制工具/模式激活时不显示
    if ((isDrawingMode.value || currentDrawingTool.value) && !isToolActive) {
      return false;
    }
    // 点工具绘制过程中显示
    if (isDrawingMode.value && isToolActive) {
      return true;
    }
    // 草图模式下选中点实体时显示
    if (isSketchActive.value && hasPointSelected.value) {
      return true;
    }
    return false;
  });

  /** 插入线条面板是否可见（线工具激活且 lineDrawingPointCount=0） */
  const insertLinePropertyVisible = computed<boolean>(() => {
    // 多实体关系面板候选态激活时不显示
    if (options.multipleEntitiesBoxSelected.value) {
      return false;
    }
    const isLineToolActive = currentDrawingTool.value === SketchDrawingTool.Line;
    // 线工具激活且尚未开始绘制时显示
    return isLineToolActive && (lineDrawingPointCount.value ?? 0) === 0;
  });

  return {
    linePropertyVisible,
    arcPropertyVisible,
    circlePropertyVisible,
    ellipsePropertyVisible,
    polygonPropertyVisible,
    rectanglePropertyVisible,
    splinePropertyVisible,
    styleSplinePropertyVisible,
    slotPropertyVisible,
    pointPropertyVisible,
    insertLinePropertyVisible,
  };
}
